/* eslint-disable no-param-reassign */
import { toPathParts } from '@tencent/data-mapper';
/**
 * 输入一个 compPath，将其转化为它相对于 UICore.Pagelet 的位置
 *
 * 例如 `/default/2` 对应的就是 `["layout", "children", 2]`
 *
 * 这里还支持扩展的语法：
 *
 * - `/@triggers/0` 指向 trigger
 * - `/default/0/#foobar/default/2` 注意这里指向了 scopedSlot foobar  --- 这个语法现实中目前还没启用！！！
 *
 * @param {string} compPath
 * @return {string[]}
 */
export function compPathToSourcePath(compPath) {
  const parts = (compPath || '').split('/').slice(1); // 去掉开头的 空字符串
  const ans = ['layout']; // 一般都是从 layout 来的，除了 @triggers
  for (let i = 0; i < parts.length; i += 2) {
    const slotId = parts[i];
    const slotIndex = parts[i + 1];
    // -----------------------
    if (!slotId) break;
    // 遇到了作用域插槽，这个比较特殊，会错开一位，所以需要弄到这里先处理
    if (slotId[0] === '#') {
      ans.push('scopedSlots', slotId.slice(1), 'layout');
      i -= 1;
      continue;
    }
    // -----------------------
    if (!slotIndex) break;
    if (slotId[0] === '@') {
      // 遇到了 `@triggers/0` 这种扩展语法，那就不是在 layout 下面了，需把末尾的 layout 抹掉
      ans.splice(-1, 1, slotId.slice(1), slotIndex);
      continue;
    }
    if (slotId === 'default') {
      ans.push('children', slotIndex);
      continue;
    }
    ans.push('slots', slotId, slotIndex);
  }
  return ans;
}
/**
 * 这个相当于 compPathToSourcePath 的反向操作
 *
 * 注意，这个可能会丢失一些信息！比如你是在 `["layout", "children", 2, "events", "click"]` 那么就会返回 `/default/2` 而丢失“当前正在编辑 events.click” 的信息
 *
 * @param  sourcePath 相对于根 UICore.Pagelet 的位置
 *   - 可以是字符串，例如 `/layout/children/2`
 *   - 可以是数组，例如 `["layout", "children", 2, "events", "click"]`
 *
 * @return {string | null} 对应的 compPath。如果没有对应的组件，这个会返回 `null`
 */
export function sourcePathToCompPath(sourcePath) {
  let place = Array.isArray(sourcePath) ? sourcePath.slice(0) : toPathParts(sourcePath);
  let compPath = '';
  // ---------------
  // 首先去掉一些开头的东西，让 `place` 指向第一个 Layout
  if (place[0] === 'layout' && place[1] === 'triggers' && place.length >= 3) {
    place.shift();
  }
  if (place[0] === 'triggers' && place.length >= 2) {
    compPath = `/@triggers/${place[1]}`;
    place = place.slice(2);
  } else if (place[0] === 'layout') {
    place = place.slice(1);
  } else {
    // 不存在指向的 Layout
    return null;
  }
  // ---------------
  // 然后开始根据 place 进行匹配，一点点构造
  const mLen = place.length;
  for (let i = 0; i < mLen; i++) {
    const m1 = place[i];
    let slotId;
    let slotIndex;
    if (m1 === 'children' && i <= mLen - 2) {
      slotId = 'default';
      slotIndex = place[i + 1];
      i += 1; // 跳过后面的 index
    }
    if (m1 === 'slots' && i <= mLen - 3) {
      slotId = place[i + 1];
      slotIndex = place[i + 2];
      i += 2; // 跳过后面的 index
    }
    if (m1 === 'scopedSlots' && i <= mLen - 2) {
      // 接下来是作用域插槽了
      // 递归下去找
      slotId = place[i + 1];
      const subAnswer = sourcePathToCompPath(place.slice(i + 2));
      if (typeof subAnswer === 'string') compPath += `#${slotId}${subAnswer}`;
      break;
    }
    if (typeof slotId === 'string' && !Number.isNaN(+slotIndex)) compPath += `/${slotId}/${slotIndex}`;else break; // 无法构造 CompPath 了
  }
  return compPath;
}
/**
 * 用于获取某个 CompPath 所属的 Layout 数组，指向的 sourcePath
 *
 * @param {string} compPath
 * @returns {string[] | null}
 */
export function getSourcePathOfSlot(compPath) {
  const sourcePath = compPathToSourcePath(compPath);
  const idxKey = sourcePath.pop();
  if (idxKey === 'layout') return null; // "" 或者 "/default/0/#scopedSlot" 这种场景
  return sourcePath; // 此时 index 被去掉了
}
/**
 * @param {string} path
 */
export function splitCompPath(path) {
  const ans = [];
  const indices = path.substr(1).split('/');
  let compPath = '';
  while (indices.length >= 2) {
    const slotId = indices.shift();
    const index = +indices.shift();
    const parentCompPath = compPath;
    compPath = `${compPath}/${slotId}/${index}`;
    ans.push({
      slotId,
      index,
      compPath,
      parentCompPath
    });
  }
  return ans;
}
/**
 * @param {string} path
 */
export function getCompPathParentInfo(path) {
  if (!path) return null;
  const ans = /^(.*)\/([^/]+)\/(\d+)$/.exec(path);
  if (!ans) return null;
  return {
    slotId: String(ans[2]),
    index: +ans[3],
    compPath: path,
    parentCompPath: String(ans[1])
  };
}
/**
 * 当一个layout被修改时，某一个 compPath 可能会发生变化（比如它的前面被插入了什么东西之类的情况）
 *
 * @param {string} compPath
 * @param {{ action: "delete" | "insertBefore", compPath: string }[]} actions
 * @returns {string | null} 如果是 null 就表示当前组件不复存在了
 */
export function computeNewCompPath(compPath, actions) {
  const S = splitCompPath(compPath);
  let bailAll = false; // 当前组件已经不复存在了
  actions.forEach(({
    action,
    compPath: stepCompPath
  }) => {
    if (bailAll) return;
    const T = splitCompPath(stepCompPath);
    for (let i = 0; i < T.length && i < S.length; i++) {
      const S0 = S[i];
      const T0 = T[i];
      if (T0.slotId !== S0.slotId) break; // 不在一个 slot 上可忽略
      if (T0.index > S0.index) break; // 动了curr之后的，影响不到了
      if (T0.index < S0.index) {
        // 动了curr之前的...
        if (i === T.length - 1) {
          // 如果是动了[curr之前的东西]的子元素，就无需特殊操作
          if (action === 'delete') S0.index -= 1;
          if (action === 'insertBefore') S0.index += 1;
        }
        break;
      }
      // slotId 和 index 相同
      // 接下来可能要继续深挖，也可能要提前终止
      if (i === T.length - 1) {
        // 整个S被销毁了 or 就在当前之前插入
        if (action === 'delete') bailAll = true;
        if (action === 'insertBefore') S0.index += 1;
        break;
      }
      if (i === S.length - 1) {
        // 当前操作的是 S 下面的某个插槽的东西，而我们的关注点是S，所以对S整体而言不受影响
        break;
      }
      // 其他情况就要继续下钻
    }
  });
  if (bailAll) return null;
  return S.map(x => `/${x.slotId}/${x.index}`).join('');
}
export function escapeSlotName(slotName) {
  return slotName.replace(/\/|\x1B/g, ch => `\x1B${`00${ch.charCodeAt(0).toString(16)}`.slice(-2)}`);
}
export function unescapeSlotName(slotName) {
  return slotName.replace(/\x1B([0-9a-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}
//# sourceMappingURL=path.js.map
